import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  listMyTravelPacks,
  packKindMeta,
  type PackProgramDay,
  type SavedPack,
  type SavedPackItem,
} from '@/lib/travelPack';
import type { ZifekUser } from '@/hooks/useAuth';

// ─────────────────────────────────────────────────────────────
// « Mes packs » — chaque voyage réservé via l'assistant IA, avec
// l'état de chaque prestation auprès de son commerce, et des
// filtres par commerce / par statut de réservation.
// ─────────────────────────────────────────────────────────────

interface Props {
  user: ZifekUser;
}

type StatusKey = 'all' | 'confirmed' | 'pending' | 'cancelled';

const STATUS_TABS: { key: StatusKey; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'confirmed', label: 'Confirmés' },
  { key: 'pending', label: 'En attente' },
  { key: 'cancelled', label: 'Annulés' },
];

interface OrderStatusRow {
  id: number;
  statut_com_vendeur: string | null;
  statut_com_client: string | null;
  sommepayee: string | null;
}

function money(value: number, currency: string): string {
  return `${(Number(value) || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} ${currency}`;
}

function frDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(`${String(iso).slice(0, 10)}T00:00:00`).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return String(iso);
  }
}

/** Traduit un statut de commande en statut lisible de réservation. */
function itemStatusLabel(order: OrderStatusRow | undefined, item: SavedPackItem): {
  label: string;
  className: string;
  icon: string;
} {
  if (item.reservationId && !order) {
    return { label: 'Réservation envoyée', className: 'bg-secondary-100 text-secondary-900', icon: 'ri-hotel-bed-line' };
  }
  const raw = String(order?.statut_com_vendeur || order?.statut_com_client || '').toLowerCase();
  if (/annul|cancel/.test(raw)) {
    return { label: 'Annulé', className: 'bg-red-100 text-red-700', icon: 'ri-close-circle-line' };
  }
  if (/livr|confirm|pay|termin|valid/.test(raw) || Number(order?.sommepayee) > 0) {
    return { label: 'Confirmé', className: 'bg-accent-100 text-accent-800', icon: 'ri-checkbox-circle-line' };
  }
  return { label: 'En attente de confirmation', className: 'bg-amber-100 text-amber-800', icon: 'ri-time-line' };
}

function ProgramList({ program }: { program: PackProgramDay[] }) {
  if (program.length === 0) return null;
  return (
    <div className="mt-4 pt-4 border-t border-background-200/60">
      <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-foreground-400 mb-3">
        Programme jour par jour
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {program.map((day) => (
          <div key={day.day} className="rounded-lg bg-background-100/60 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="w-6 h-6 rounded-full bg-accent-500 text-background-50 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                J{day.day}
              </span>
              <span className="text-[11px] text-foreground-500">{frDate(day.date)}</span>
            </div>
            <p className="text-[12px] font-semibold text-foreground-900 mb-1">{day.title}</p>
            <ul className="space-y-0.5">
              {day.lines.map((line) => (
                <li key={line} className="text-[11px] text-foreground-600 flex items-start gap-1.5">
                  <i className="ri-checkbox-blank-circle-fill text-[4px] mt-1.5 text-accent-500 flex-shrink-0"></i>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PacksTab({ user }: Props) {
  const [packs, setPacks] = useState<SavedPack[]>([]);
  const [statuses, setStatuses] = useState<Record<number, OrderStatusRow>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [merchantFilter, setMerchantFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<StatusKey>('all');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    setError(null);
    try {
      const rows = await listMyTravelPacks(user.id);
      setPacks(rows);
      const orderIds = Array.from(new Set(
        rows.flatMap((p) => p.items.map((i) => i.orderId)).filter((value): value is number => Boolean(value)),
      ));
      if (orderIds.length > 0) {
        const { data } = await supabase
          .from('commande')
          .select('id, statut_com_vendeur, statut_com_client, sommepayee')
          .in('id', orderIds);
        const map: Record<number, OrderStatusRow> = {};
        ((data || []) as OrderStatusRow[]).forEach((row) => { map[Number(row.id)] = row; });
        setStatuses(map);
      } else {
        setStatuses({});
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger vos packs.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  /** Statut lisible d'une prestation (utilisé par les filtres). */
  const statusKey = (item: SavedPackItem): 'confirmed' | 'pending' | 'cancelled' => {
    const label = itemStatusLabel(item.orderId ? statuses[item.orderId] : undefined, item).label;
    if (label === 'Confirmé') return 'confirmed';
    if (label === 'Annulé') return 'cancelled';
    return 'pending';
  };

  const merchantOptions = Array.from(new Set(
    packs.flatMap((pack) => pack.items.map((item) => item.merchantName || '').filter(Boolean)),
  )).sort((a, b) => a.localeCompare(b));

  const itemMatches = (item: SavedPackItem): boolean => {
    if (merchantFilter !== 'all' && (item.merchantName || '') !== merchantFilter) return false;
    if (statusFilter !== 'all' && statusKey(item) !== statusFilter) return false;
    return true;
  };

  const visiblePacks = packs
    .map((pack) => ({ pack, items: pack.items.filter(itemMatches) }))
    .filter((entry) => entry.items.length > 0);

  const hasActiveFilter = merchantFilter !== 'all' || statusFilter !== 'all';

  const resetFilters = () => {
    setMerchantFilter('all');
    setStatusFilter('all');
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <i className="ri-error-warning-line text-3xl text-accent-600"></i>
          <p className="mt-2 text-sm text-foreground-600">{error}</p>
          <button
            type="button"
            onClick={() => void load()}
            className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer"
          >
            Réessayer
          </button>
        </div>
      );
    }

    if (packs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/50 rounded-xl text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-luggage-cart-line text-xl text-foreground-500"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-800">Aucun pack pour le moment</h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-sm">
            Demandez à l&apos;assistant IA : « 4 jours à Marrakech, budget 3000 dirhams » — il compose le voyage,
            vous n&apos;avez plus qu&apos;à tout réserver.
          </p>
        </div>
      );
    }

    if (visiblePacks.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/50 rounded-xl text-center px-6">
          <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-filter-off-line text-xl text-foreground-500"></i>
          </div>
          <h3 className="text-sm font-semibold text-foreground-800">Aucune prestation ne correspond</h3>
          <p className="text-xs text-foreground-500 mt-1 max-w-sm">
            Changez de commerce ou de statut pour retrouver vos réservations.
          </p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold cursor-pointer"
          >
            Réinitialiser les filtres
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {visiblePacks.map(({ pack, items: packItems }) => {
          const isOpen = openId === pack.id;
          const confirmed = pack.items.filter((item) => {
            const st = itemStatusLabel(item.orderId ? statuses[item.orderId] : undefined, item);
            return st.label === 'Confirmé';
          }).length;
          return (
            <div key={pack.id} className="bg-background-50 border border-background-200/60 rounded-xl overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenId(isOpen ? null : pack.id)}
                className="w-full flex flex-wrap items-center gap-3 px-4 md:px-5 py-4 text-left hover:bg-background-100/40 transition-colors cursor-pointer"
              >
                <span className="w-11 h-11 rounded-xl bg-accent-100 text-accent-800 flex items-center justify-center flex-shrink-0">
                  <i className="ri-luggage-cart-line text-lg"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold font-heading text-foreground-950 truncate">{pack.destination}</p>
                  <p className="text-[11px] text-foreground-500">
                    {frDate(pack.checkIn)} → {frDate(pack.checkOut)} · {pack.nights} nuit{pack.nights > 1 ? 's' : ''} · {pack.days} jour{pack.days > 1 ? 's' : ''} · {pack.guests} pers.
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-foreground-950 whitespace-nowrap">{money(pack.total, pack.currency)}</p>
                  <p className="text-[10px] text-foreground-400 whitespace-nowrap">
                    {confirmed}/{pack.items.length} confirmé{confirmed > 1 ? 's' : ''}
                  </p>
                </div>
                <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
              </button>

              {isOpen && (
                <div className="px-4 md:px-5 pb-5 border-t border-background-200/60">
                  <div className="divide-y divide-background-200/50">
                    {packItems.map((item, index) => {
                      const meta = packKindMeta(item.kind);
                      const status = itemStatusLabel(item.orderId ? statuses[item.orderId] : undefined, item);
                      return (
                        <div key={`${item.kind}-${index}`} className="flex items-start gap-3 py-3">
                          <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0">
                            <i className={`${meta.icon} text-sm`}></i>
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-700">{meta.label}</span>
                              {item.merchantName && (
                                <span className="text-[10px] text-foreground-400 truncate">· {item.merchantName}</span>
                              )}
                            </div>
                            <p className="text-sm font-semibold text-foreground-900 leading-snug">{item.name}</p>
                            <p className="text-[11px] text-foreground-500 mt-0.5">{item.detail || ''}</p>
                            <span className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${status.className}`}>
                              <i className={status.icon}></i>{status.label}
                            </span>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-bold text-foreground-900 whitespace-nowrap">
                              {item.price > 0 ? money(item.price, item.currency) : 'Sur devis'}
                            </p>
                            {item.orderId && (
                              <p className="text-[10px] text-foreground-400">commande #{item.orderId}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <ProgramList program={pack.program} />

                  {pack.notes.length > 0 && (
                    <div className="mt-4 rounded-lg bg-secondary-50/70 p-3">
                      {pack.notes.map((note) => (
                        <p key={note} className="text-[11px] text-secondary-900 flex items-start gap-1.5 py-0.5">
                          <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                          <span>{note}</span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Mes packs</h2>
          <p className="text-xs text-foreground-500 mt-1">
            Vos voyages composés avec l&apos;assistant IA et l&apos;état de chaque réservation par commerce.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50 border border-background-200/70 text-xs font-semibold text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line"></i>Actualiser
        </button>
      </div>

      {packs.length > 0 && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-5">
          <div className="relative flex-1 min-w-0">
            <i className="ri-store-2-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <select
              value={merchantFilter}
              onChange={(e) => setMerchantFilter(e.target.value)}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-background-200/70 bg-background-50 text-xs font-medium text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer"
            >
              <option value="all">Tous les commerces</option>
              {merchantOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1 p-1 bg-background-100 rounded-full w-fit">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                  statusFilter === tab.key ? 'bg-background-50 text-foreground-950' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {hasActiveFilter && (
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-foreground-500 hover:text-foreground-800 hover:bg-background-100 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line"></i>
              Réinitialiser
            </button>
          )}
        </div>
      )}

      {renderContent()}
    </div>
  );
}